import React, { useState, useEffect } from 'react';
import CloseIcon from '@material-ui/icons/Close';
import LinkIcon from '@material-ui/icons/Link';
import AddIcon from '@material-ui/icons/Add';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import ScheduleIcon from '@material-ui/icons/Schedule';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import LinkOffIcon from '@material-ui/icons/LinkOff';
import BlockIcon from '@material-ui/icons/Block';
import ReplayIcon from '@material-ui/icons/Replay';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import CircularProgress from '@material-ui/core/CircularProgress';
import { axios, endpoints } from 'Api';
import styles from './UploadLinkModalClassic.module.css';

const EXPIRY_OPTIONS = [
  { value: 'hours', label: 'Hours', options: [3, 6, 12, 24] },
  { value: 'days', label: 'Days', options: [1, 3, 7, 30] },
];

const UploadLinkModalClassic = ({
  open, onClose, onSuccess,
  // When set, the modal opens straight into the edit view for THIS link
  // (used by the Shared-tab folder edit) instead of the list. onUpdated is
  // called with { uuid, label, expires_at } after a successful save.
  editLink = null, onUpdated,
}) => {
  const [view, setView] = useState('loading'); // 'loading', 'list', 'create', 'edit', 'success'
  // True when opened directly to edit a single link — Back/Save close the
  // modal instead of returning to the (unfetched) list.
  const [editOnly, setEditOnly] = useState(false);
  const [existingLinks, setExistingLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(true);

  const [expiryType, setExpiryType] = useState('hours');
  const [expiryValue, setExpiryValue] = useState(24);
  const [noExpiry, setNoExpiry] = useState(true);
  const [label, setLabel] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [copiedLinkId, setCopiedLinkId] = useState(null);
  const [error, setError] = useState('');
  const [savedExpiryDate, setSavedExpiryDate] = useState(null);
  const [revokingLinkId, setRevokingLinkId] = useState(null);
  const [reactivatingLinkId, setReactivatingLinkId] = useState(null);
  // Link pending revoke confirmation (the whole link object, or null when no
  // confirm dialog is open). Revoke never fires on a single click — the user
  // must confirm in the dialog first.
  const [revokeTarget, setRevokeTarget] = useState(null);
  // Edit-link state
  const [editingLink, setEditingLink] = useState(null);
  const [updateExpiry, setUpdateExpiry] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch existing links when modal opens
  const fetchExistingLinks = async () => {
    setLoadingLinks(true);
    try {
      const response = await axios.post(endpoints.listContributionLinks, {
        status: 'all',
      });
      if (response.data && response.data.links) {
        setExistingLinks(response.data.links);
        setView(response.data.links.length > 0 ? 'list' : 'create');
      } else {
        setExistingLinks([]);
        setView('create');
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error fetching links:', err);
      setExistingLinks([]);
      setView('create');
    } finally {
      setLoadingLinks(false);
    }
  };

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setExpiryType('hours');
      setExpiryValue(24);
      setGeneratedLink('');
      setCopied(false);
      setCopiedLinkId(null);
      setError('');
      setSavedExpiryDate(null);
      setRevokingLinkId(null);
      setReactivatingLinkId(null);
      setIsSaving(false);
      if (editLink) {
        // Jump straight into the edit view for the given link.
        setEditOnly(true);
        setEditingLink(editLink);
        setLabel(editLink.label || '');
        setNoExpiry(!editLink.expires_at);
        setUpdateExpiry(false);
        setView('edit');
      } else {
        setEditOnly(false);
        setNoExpiry(true);
        setLabel('');
        setEditingLink(null);
        setUpdateExpiry(false);
        setView('loading');
        fetchExistingLinks();
      }
    }
  }, [open, editLink]);

  // Build expiry date from selections
  const buildExpiryDate = () => {
    const date = new Date();
    if (expiryType === 'hours') {
      date.setHours(date.getHours() + expiryValue);
    } else {
      date.setDate(date.getDate() + expiryValue);
    }
    return date;
  };

  // Get current expiry options based on type
  const getCurrentOptions = () => {
    const option = EXPIRY_OPTIONS.find((o) => o.value === expiryType);
    return option ? option.options : [];
  };

  // Format date for API (YYYY-MM-DD HH:mm:ss)
  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:00`;
  };

  // Get timezone string (e.g., "Asia/Kolkata", "America/New_York")
  const getTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Format date for display
  const formatDateForDisplay = (date) => date.toLocaleString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  // Format short date for list
  const formatShortDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Check if link is expired
  const isLinkExpired = (link) => link.is_expired === 1 || link.is_expired === true;

  // Check if link is revoked (is_active can be "0", "1", 0, 1, or boolean)
  const isLinkRevoked = (link) => link.is_active === 0 || link.is_active === '0' || link.is_active === false;

  // Get link status
  const getLinkStatus = (link) => {
    if (isLinkRevoked(link)) return 'revoked';
    if (isLinkExpired(link)) return 'expired';
    return 'active';
  };

  // Handle expiry type change
  const handleExpiryTypeChange = (type) => {
    setExpiryType(type);
    if (type === 'hours') {
      setExpiryValue(24);
    } else {
      setExpiryValue(1);
    }
  };

  const handleCreateLink = async () => {
    if (!label.trim()) {
      setError('Please enter a label for this link.');
      return;
    }

    const expiryDateTime = noExpiry ? null : buildExpiryDate();
    setSavedExpiryDate(expiryDateTime);
    setIsCreating(true);
    setError('');

    try {
      const response = await axios.post(endpoints.createContributionLink, {
        expires_at: noExpiry ? null : formatDateForAPI(expiryDateTime),
        timezone: getTimezone(),
        label: label.trim(),
      });

      if (response.data && response.data.link) {
        setGeneratedLink(response.data.link);
        setView('success');

        // Auto-copy to clipboard
        try {
          await navigator.clipboard.writeText(response.data.link);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch (clipboardError) {
          // eslint-disable-next-line no-console
          console.log('Auto-copy failed, user can manually copy');
        }

        if (onSuccess) {
          onSuccess(response.data.link);
        }
      } else {
        setError('Failed to generate link. Please try again.');
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error creating contribution link:', err);
      setError(err.response?.data?.message || 'Failed to create link. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopyLink = async (link, linkId = null) => {
    try {
      await navigator.clipboard.writeText(link);
      if (linkId) {
        setCopiedLinkId(linkId);
        setTimeout(() => setCopiedLinkId(null), 2000);
      } else {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to copy:', err);
    }
  };

  const handleRevokeLink = async (linkUuid) => {
    setRevokingLinkId(linkUuid);
    try {
      await axios.post(endpoints.revokeContributionLink, {
        uuid: linkUuid,
      });
      // Refresh the list
      await fetchExistingLinks();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error revoking link:', err);
      setError(err.response?.data?.message || 'Failed to revoke link.');
    } finally {
      setRevokingLinkId(null);
    }
  };

  // Called from the confirmation dialog's Revoke button. Closes the dialog,
  // then proceeds with the actual revoke.
  const confirmRevoke = async () => {
    const target = revokeTarget;
    setRevokeTarget(null);
    if (target) {
      await handleRevokeLink(target.uuid);
    }
  };

  const handleReactivateLink = async (linkUuid) => {
    setReactivatingLinkId(linkUuid);
    setError('');
    try {
      await axios.post(endpoints.reactivateContributionLink, {
        uuid: linkUuid,
      });
      // Refresh the list
      await fetchExistingLinks();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error reactivating link:', err);
      setError(err.response?.data?.message || 'Failed to reactivate link.');
    } finally {
      setReactivatingLinkId(null);
    }
  };

  // Open the edit view pre-filled from the chosen link
  const handleStartEdit = (link) => {
    setEditingLink(link);
    setLabel(link.label || '');
    setNoExpiry(!link.expires_at);
    setUpdateExpiry(false);
    setExpiryType('hours');
    setExpiryValue(24);
    setError('');
    setView('edit');
  };

  const handleSaveEdit = async () => {
    if (!label.trim()) {
      setError('Please enter a folder name.');
      return;
    }
    setIsSaving(true);
    setError('');
    try {
      const payload = { uuid: editingLink.uuid, label: label.trim() };
      // Only touch expiry when the user explicitly chose to change it
      if (updateExpiry) {
        if (noExpiry) {
          payload.expires_at = null;
        } else {
          payload.expires_at = formatDateForAPI(buildExpiryDate());
          payload.timezone = getTimezone();
        }
      }
      await axios.post(endpoints.editContributionLink, payload);
      if (editOnly) {
        // Opened just to edit this one folder — hand the new values back to
        // the caller (Shared tab) so it can patch its list, then close.
        const newExpires = updateExpiry
          ? (noExpiry ? null : payload.expires_at)
          : (editingLink?.expires_at ?? null);
        if (onUpdated) onUpdated({ uuid: editingLink.uuid, label: label.trim(), expires_at: newExpires });
        setIsSaving(false);
        onClose();
        return;
      }
      setEditingLink(null);
      setView('list');
      await fetchExistingLinks();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error editing link:', err);
      setError(err.response?.data?.message || 'Failed to update folder. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setGeneratedLink('');
    setCopied(false);
    setError('');
    onClose();
  };

  const handleBackToList = () => {
    setView('list');
    setGeneratedLink('');
    setCopied(false);
    setError('');
    fetchExistingLinks();
  };

  if (!open) return null;

  return (
    <>
    <div
      className={styles.overlay}
      onClick={handleClose}
      onKeyDown={(e) => e.key === 'Escape' && handleClose()}
      role="button"
      tabIndex={0}
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <LinkIcon style={{ fontSize: 24, color: '#5aa5ef', marginRight: 12 }} />
            <h2 className={styles.title}>
              {view === 'list' ? 'Shared Folders' : view === 'create' ? 'Create Shared Folder' : view === 'edit' ? 'Edit Shared Folder' : view === 'success' ? 'Shared Folder Created' : 'Shared Folders'}
            </h2>
          </div>
          <div className={styles.headerRight}>
            {view === 'list' && (
              <button
                type="button"
                className={styles.newBtn}
                onClick={() => {
                  setLabel('');
                  setNoExpiry(true);
                  setExpiryType('hours');
                  setExpiryValue(24);
                  setError('');
                  setView('create');
                }}
              >
                <AddIcon style={{ fontSize: 18 }} />
                New
              </button>
            )}
            <button className={styles.closeButton} onClick={handleClose} type="button">
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {/* Loading State */}
          {view === 'loading' && (
            <div className={styles.loadingContainer}>
              <CircularProgress size={40} style={{ color: '#5aa5ef' }} />
              <span className={styles.loadingText}>Loading links...</span>
            </div>
          )}

          {/* List View */}
          {view === 'list' && (
            <div className={styles.listView}>
              {/* Links List */}
              <div className={styles.linksList}>
                {existingLinks.map((link) => {
                  const status = getLinkStatus(link);
                  const linkUrl = link.short_link || link.link;
                  return (
                    <div
                      key={link.uuid}
                      className={`${styles.linkItem} ${status !== 'active' ? styles.inactive : ''}`}
                    >
                      <div className={styles.linkItemMain}>
                        <div className={styles.linkUrlRow}>
                          <span className={styles.linkUrl} title={link.label || linkUrl}>
                            {link.label || linkUrl}
                          </span>
                        </div>
                        <div className={styles.linkMeta}>
                          <span className={`${styles.statusBadge} ${styles[status]}`}>
                            {status === 'active' ? 'Active' : status === 'expired' ? 'Expired' : 'Revoked'}
                          </span>
                          <span className={styles.linkExpiry}>
                            {status === 'revoked'
                              ? 'Revoked'
                              : !link.expires_at
                                ? 'No expiry'
                                : `${status === 'expired' ? 'Expired on ' : 'Expires on '}${formatShortDate(link.expires_at)}`}
                          </span>
                          {parseInt(link.contribution_count, 10) > 0 && (
                            <span className={styles.linkCount}>
                              {link.contribution_count}
                              {' '}
                              {parseInt(link.contribution_count, 10) === 1 ? 'upload' : 'uploads'}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className={styles.linkActions}>
                        <button
                          type="button"
                          className={`${styles.actionBtn} ${styles.editBtn}`}
                          onClick={() => handleStartEdit(link)}
                          title="Edit folder"
                        >
                          <EditOutlinedIcon style={{ fontSize: 16 }} />
                        </button>
                        {status === 'active' && (
                          <button
                            type="button"
                            className={`${styles.actionBtn} ${styles.copyBtn} ${copiedLinkId === link.uuid ? styles.copied : ''}`}
                            onClick={() => handleCopyLink(linkUrl, link.uuid)}
                            title="Copy link"
                          >
                            <FileCopyOutlinedIcon style={{ fontSize: 16 }} />
                          </button>
                        )}
                        {status === 'active' && (
                          <button
                            type="button"
                            className={`${styles.actionBtn} ${styles.revokeBtn}`}
                            onClick={() => setRevokeTarget(link)}
                            disabled={revokingLinkId === link.uuid}
                            title="Revoke link"
                          >
                            {revokingLinkId === link.uuid ? (
                              <CircularProgress size={16} style={{ color: '#f44336' }} />
                            ) : (
                              <LinkOffIcon style={{ fontSize: 16 }} />
                            )}
                          </button>
                        )}
                        {status === 'revoked' && (
                          <button
                            type="button"
                            className={`${styles.actionBtn} ${styles.reactivateBtn}`}
                            onClick={() => handleReactivateLink(link.uuid)}
                            disabled={reactivatingLinkId === link.uuid}
                            title="Reactivate link"
                          >
                            {reactivatingLinkId === link.uuid ? (
                              <CircularProgress size={16} style={{ color: '#10b981' }} />
                            ) : (
                              <ReplayIcon style={{ fontSize: 16 }} />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Create View */}
          {view === 'create' && (
            <>
              {/* Info Section */}
              <div className={styles.infoSection}>
                <p className={styles.infoTitle}>
                  <InfoOutlinedIcon style={{ fontSize: 18 }} />
                  How it works
                </p>
                <div className={styles.infoBullet}>
                  Allow anyone to add videos or photos directly to your shared folder
                </div>
                <div className={styles.infoBullet}>
                  Share this folder link with family, friends, or collaborators
                </div>
                <div className={styles.infoBullet}>
                  Added content will appear in your Shared tab for review
                </div>
                <div className={styles.infoBullet}>
                  The folder link expires after the time you set for security
                </div>
              </div>

              {/* Form Section */}
              <div className={styles.formSection}>
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel} htmlFor="upload-link-label">
                    <InfoOutlinedIcon style={{ fontSize: 16 }} />
                    Folder Name
                    <span style={{ color: '#f44336' }}>*</span>
                  </label>
                  <input
                    id="upload-link-label"
                    type="text"
                    className={styles.textInput}
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="e.g. Wedding photos"
                    maxLength={100}
                    required
                  />
                </div>

                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>
                    <ScheduleIcon style={{ fontSize: 16 }} />
                    Folder Link Expires In
                  </label>
                  <label className={styles.noExpiryRow}>
                    <input
                      type="checkbox"
                      checked={noExpiry}
                      onChange={(e) => setNoExpiry(e.target.checked)}
                    />
                    No expiry (folder link never expires)
                  </label>
                  {!noExpiry && (
                    <>
                      <div className={styles.expiryPickerRow}>
                        <div className={styles.expiryTypeToggle}>
                          <button
                            type="button"
                            className={`${styles.toggleBtn} ${expiryType === 'hours' ? styles.active : ''}`}
                            onClick={() => handleExpiryTypeChange('hours')}
                          >
                            Hours
                          </button>
                          <button
                            type="button"
                            className={`${styles.toggleBtn} ${expiryType === 'days' ? styles.active : ''}`}
                            onClick={() => handleExpiryTypeChange('days')}
                          >
                            Days
                          </button>
                        </div>
                        <select
                          className={styles.expirySelect}
                          value={expiryValue}
                          onChange={(e) => setExpiryValue(Number(e.target.value))}
                        >
                          {getCurrentOptions().map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                              {' '}
                              {expiryType === 'hours' ? (opt === 1 ? 'hour' : 'hours') : (opt === 1 ? 'day' : 'days')}
                            </option>
                          ))}
                        </select>
                      </div>
                      <span className={styles.expiryHint}>
                        Folder link will expire on
                        {' '}
                        {formatDateForDisplay(buildExpiryDate())}
                      </span>
                    </>
                  )}
                </div>

                {error && (
                  <p style={{ color: '#f44336', fontSize: 13, margin: 0 }}>{error}</p>
                )}

                <div className={styles.formActions}>
                  {existingLinks.length > 0 && (
                    <button
                      type="button"
                      className={styles.backBtn}
                      onClick={() => setView('list')}
                    >
                      Back to Folders
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleCreateLink}
                    disabled={isCreating || !label.trim()}
                    className={styles.createButton}
                  >
                    {isCreating ? (
                      <>
                        <CircularProgress size={18} style={{ color: '#fff', marginRight: 8 }} />
                        Creating...
                      </>
                    ) : (
                      'Create Shared Folder'
                    )}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Edit View */}
          {view === 'edit' && (
            <div className={styles.formSection}>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel} htmlFor="edit-link-label">
                  <InfoOutlinedIcon style={{ fontSize: 16 }} />
                  Folder Name
                  <span style={{ color: '#f44336' }}>*</span>
                </label>
                <input
                  id="edit-link-label"
                  type="text"
                  className={styles.textInput}
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g. Wedding photos"
                  maxLength={100}
                  required
                />
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>
                  <ScheduleIcon style={{ fontSize: 16 }} />
                  Expiry
                </label>
                <label className={styles.noExpiryRow}>
                  <input
                    type="checkbox"
                    checked={updateExpiry}
                    onChange={(e) => setUpdateExpiry(e.target.checked)}
                  />
                  Change expiry
                </label>
                {!updateExpiry && (
                  <span className={styles.expiryHint}>
                    {`Current: ${editingLink?.expires_at ? formatShortDate(editingLink.expires_at) : 'No expiry'}`}
                  </span>
                )}
                {updateExpiry && (
                  <>
                    <label className={styles.noExpiryRow}>
                      <input
                        type="checkbox"
                        checked={noExpiry}
                        onChange={(e) => setNoExpiry(e.target.checked)}
                      />
                      No expiry (folder link never expires)
                    </label>
                    {!noExpiry && (
                      <>
                        <div className={styles.expiryPickerRow}>
                          <div className={styles.expiryTypeToggle}>
                            <button
                              type="button"
                              className={`${styles.toggleBtn} ${expiryType === 'hours' ? styles.active : ''}`}
                              onClick={() => handleExpiryTypeChange('hours')}
                            >
                              Hours
                            </button>
                            <button
                              type="button"
                              className={`${styles.toggleBtn} ${expiryType === 'days' ? styles.active : ''}`}
                              onClick={() => handleExpiryTypeChange('days')}
                            >
                              Days
                            </button>
                          </div>
                          <select
                            className={styles.expirySelect}
                            value={expiryValue}
                            onChange={(e) => setExpiryValue(Number(e.target.value))}
                          >
                            {getCurrentOptions().map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                                {' '}
                                {expiryType === 'hours' ? (opt === 1 ? 'hour' : 'hours') : (opt === 1 ? 'day' : 'days')}
                              </option>
                            ))}
                          </select>
                        </div>
                        <span className={styles.expiryHint}>
                          New expiry
                          {' '}
                          {formatDateForDisplay(buildExpiryDate())}
                        </span>
                      </>
                    )}
                  </>
                )}
              </div>

              {error && (
                <p style={{ color: '#f44336', fontSize: 13, margin: 0 }}>{error}</p>
              )}

              <div className={styles.formActions}>
                <button
                  type="button"
                  className={styles.backBtn}
                  onClick={() => {
                    if (editOnly) { onClose(); return; }
                    setEditingLink(null);
                    setView('list');
                    setError('');
                  }}
                >
                  {editOnly ? 'Cancel' : 'Back to Folders'}
                </button>
                <button
                  type="button"
                  onClick={handleSaveEdit}
                  disabled={isSaving || !label.trim()}
                  className={styles.createButton}
                >
                  {isSaving ? (
                    <>
                      <CircularProgress size={18} style={{ color: '#fff', marginRight: 8 }} />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Success View */}
          {view === 'success' && (
            <div className={styles.successSection}>
              <div className={styles.successHeader}>
                <CheckCircleIcon className={styles.successIcon} style={{ fontSize: 28 }} />
                <span className={styles.successText}>
                  Shared folder created and link copied to clipboard!
                </span>
              </div>

              <div className={styles.linkContainer}>
                <span className={styles.linkLabel}>Share this folder link</span>
                <div className={styles.linkInputWrapper}>
                  <input
                    type="text"
                    className={styles.linkInput}
                    value={generatedLink}
                    readOnly
                    onClick={(e) => e.target.select()}
                  />
                  <button
                    type="button"
                    className={`${styles.copyButton} ${copied ? styles.copied : ''}`}
                    onClick={() => handleCopyLink(generatedLink)}
                  >
                    <FileCopyOutlinedIcon style={{ fontSize: 16 }} />
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              <div className={styles.expiryInfo}>
                <ScheduleIcon style={{ fontSize: 18, color: '#ff9800' }} />
                <span className={styles.expiryInfoText}>
                  {savedExpiryDate ? (
                    <>
                      Expires on
                      {' '}
                      <span className={styles.expiryDate}>
                        {formatDateForDisplay(savedExpiryDate)}
                      </span>
                    </>
                  ) : (
                    <span className={styles.expiryDate}>This folder link never expires</span>
                  )}
                </span>
              </div>

              <div className={styles.successActions}>
                {existingLinks.length > 0 && (
                  <button
                    type="button"
                    className={styles.backBtn}
                    onClick={handleBackToList}
                  >
                    View All Folders
                  </button>
                )}
                <button
                  type="button"
                  className={styles.doneButton}
                  onClick={handleClose}
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Revoke confirmation dialog — only shown for active links, before the
        revoke API is called. Backdrop / Cancel = no-op; Revoke = proceed. */}
    {revokeTarget && (
      <div
        role="button"
        tabIndex={0}
        onClick={() => setRevokeTarget(null)}
        onKeyDown={(e) => e.key === 'Escape' && setRevokeTarget(null)}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
          padding: '0 32px',
          fontFamily: 'Montserrat, "Helvetica Neue", Arial, sans-serif',
        }}
      >
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
        <div
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="revoke-confirm-title"
          onClick={(e) => e.stopPropagation()}
          style={{
            width: 540,
            maxWidth: '100%',
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: 20,
            padding: '32px 32px 28px',
            boxSizing: 'border-box',
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <span
              style={{
                background: 'rgba(90,165,239,0.12)',
                padding: 11,
                borderRadius: 12,
                display: 'inline-flex',
                lineHeight: 0,
              }}
            >
              <BlockIcon style={{ fontSize: 28, color: '#5aa5ef' }} />
            </span>
            <h3
              id="revoke-confirm-title"
              style={{ margin: 0, color: '#1f2937', fontSize: 22, fontWeight: 700 }}
            >
              Revoke folder?
            </h3>
          </div>

          <p
            style={{
              margin: '20px 0 0',
              color: '#6b7280',
              fontSize: 16,
              fontWeight: 400,
              lineHeight: 1.5,
            }}
          >
            Revoking
            {' '}
            <b style={{ color: '#1f2937', fontWeight: 600 }}>
              {revokeTarget.label ? `"${revokeTarget.label}"` : 'this folder'}
            </b>
            {' '}
            will disable its share link. Contributors will no longer be able to
            upload to it. You can reactivate it later.
          </p>

          <div style={{ display: 'flex', gap: 14, marginTop: 32 }}>
            <button
              type="button"
              onClick={() => setRevokeTarget(null)}
              style={{
                flex: 1,
                height: 52,
                borderRadius: 10,
                cursor: 'pointer',
                background: '#ffffff',
                border: '1px solid #d1d5db',
                color: '#374151',
                fontSize: 15,
                fontWeight: 500,
                fontFamily: 'inherit',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmRevoke}
              style={{
                flex: 1,
                height: 52,
                borderRadius: 10,
                cursor: 'pointer',
                background: '#5aa5ef',
                border: 'none',
                color: '#ffffff',
                fontSize: 15,
                fontWeight: 600,
                fontFamily: 'inherit',
              }}
            >
              Revoke
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default UploadLinkModalClassic;
